import type { Race } from 'src/types/domain/Race'
import type { Marble } from 'src/types/domain/Marble'
import { isRaceReadyEvent } from 'src/types/domain/RaceEvent'
import type { BirdieVenue, RoundOdds } from 'src/types/domain/RoundOdds'
import { OPTIONS, PATTERNS } from 'src/lib/fairness/birdie'

export const selectRoundOdds = (race: Race | undefined): RoundOdds | undefined =>
  race?.raceEvents.find(isRaceReadyEvent)?.payload?.odds

/** race_started carries `location` (string, the toolbar pill contract) and `venue` (object, the card). */
export const selectRoundLocation = (
  race: Race | undefined
): { location?: string; venue?: BirdieVenue } | undefined => {
  const started = race?.raceEvents.find((e) => e.eventType === 'race_started') as
    | { payload?: { location?: string; venue?: BirdieVenue } }
    | undefined
  if (!started?.payload) return undefined
  const { location, venue } = started.payload
  if (location === undefined && venue === undefined) return undefined
  return {
    ...(location !== undefined ? { location } : {}),
    ...(venue !== undefined ? { venue } : {})
  }
}

/** The 12 marble rows carry `color` = canonical option key (Milestone 4). */
export const patternIdByKey = (marbles: Pick<Marble, 'uuid' | 'color'>[]): Record<string, string> =>
  Object.fromEntries(marbles.map((m) => [m.color.toUpperCase(), m.uuid]))

export function paytableInputsFromOdds(
  odds: RoundOdds,
  marbles: Pick<Marble, 'uuid' | 'color'>[]
): { weights: number[]; multipliers: number[] } | undefined {
  const ids = patternIdByKey(marbles)
  const weights: number[] = []
  for (const p of PATTERNS) {
    const w = odds.weightsPpm[ids[p] ?? '']
    if (w === undefined) return undefined
    weights.push(w)
  }
  const multipliers: number[] = []
  for (const o of OPTIONS) {
    const m = odds.multipliers[ids[o] ?? '']
    if (m === undefined) return undefined
    multipliers.push(m)
  }
  return { weights, multipliers }
}

export const BOARD_MISSING_GRACE_MS = 1500
export const BOARD_RENDERED_SAMPLE = 1 / 50

export function boardTelemetryDecision(input: {
  isBirdie: boolean
  hasReady: boolean
  hasStarted: boolean
  hasOdds: boolean
  readyAgeMs: number
  loggedRoundId?: string
  roundId: string
  random: number
  hashMatch?: boolean
}): 'missing' | 'rendered' | 'rendered_sampled_out' | 'none' {
  if (!input.isBirdie || !input.hasReady || input.loggedRoundId === input.roundId) return 'none'
  if (!input.hasOdds) {
    return !input.hasStarted && input.readyAgeMs >= BOARD_MISSING_GRACE_MS ? 'missing' : 'none'
  }
  if (input.hashMatch === false) return 'rendered'
  return input.random < BOARD_RENDERED_SAMPLE ? 'rendered' : 'rendered_sampled_out'
}
